import type { AlterationDetail } from '../api/bookings';
import { measuresFor, phrasesFor, type DocketLanguage, type MeasureVerbs } from './narrativeText';

/**
 * Turns the structured alteration rows into the prose a tailor actually works from.
 *
 * <p>
 * <strong>Structured in, narrative out — that asymmetry is the whole design.</strong> The booking
 * screen captures <code>Sleeve −1.5</code> because three other things need it as data: the
 * reversal when a fitting alteration comes back, the size recommendation that learns from it, and
 * the charge. But a tailor handed a table of signed deltas has to decode it, and a docket that
 * needs decoding is a docket that gets guessed at. So the same rows render as "Shorten both
 * sleeves by 1½ inches from the cuff".
 * </p>
 *
 * <p>
 * The alternative — a free-text instruction typed at the counter — was rejected because it makes
 * the data unavailable to all three consumers above, and because what gets typed under pressure is
 * "sleeve 1.5 short", which is the same decoding problem with worse spelling.
 * </p>
 */

/** Eighths, which is how a tailor's tape is actually marked. */
const vulgarFractions = new Map<number, string>([
  [0.125, '⅛'],
  [0.25, '¼'],
  [0.375, '⅜'],
  [0.5, '½'],
  [0.625, '⅝'],
  [0.75, '¾'],
  [0.875, '⅞'],
]);

/**
 * A measurement, written the way a tape measure reads it.
 *
 * "1½" rather than "1.5", because a workshop works in halves and eighths and a decimal is a
 * conversion somebody has to do standing up. Anything that is not a clean eighth falls back to the
 * decimal rather than being rounded — silently moving a hem by a sixteenth of an inch to make the
 * label prettier is not a trade this function is entitled to make.
 */
/**
 * A row's figure as the card prints it: a finished size bare (46½), an old change signed (−1½).
 */
export function alterationFigure(detail: AlterationDetail): string {
  if ((detail.sizeInches ?? null) !== null) {
    return formatInches(detail.sizeInches ?? 0);
  }

  const delta = detail.deltaInches ?? 0;

  return `${delta < 0 ? '−' : '+'}${formatInches(delta)}`;
}

export function formatInches(value: number): string {
  const magnitude = Math.abs(value);
  const whole = Math.floor(magnitude);
  const fraction = Number((magnitude - whole).toFixed(3));

  if (fraction === 0) {
    return String(whole);
  }

  const glyph = vulgarFractions.get(fraction);

  if (!glyph) {
    return String(magnitude);
  }

  return whole === 0 ? glyph : `${whole}${glyph}`;
}

/**
 * Per-measure wording.
 *
 * A single generic sentence — "adjust the sleeve by −1.5 inches" — was the first attempt and reads
 * like a machine talking. Each measure has a verb the trade already uses, and using it is the
 * difference between an instruction and a data dump.
 */
const measures: Record<string, MeasureVerbs> = {
  Sleeve: {
    subject: 'both sleeves',
    decrease: 'Shorten',
    increase: 'Lengthen',
    where: 'from the cuff, keeping the existing button placement',
  },
  Shoulder: { subject: 'the shoulders', decrease: 'Narrow', increase: 'Widen' },
  Chest: { subject: 'the chest', decrease: 'Take in', increase: 'Let out', where: 'at the seams' },
  Waist: {
    subject: 'the waist',
    decrease: 'Take in',
    increase: 'Let out',
    where: 'at the side seams',
  },
  Length: { subject: 'the overall length', decrease: 'Shorten', increase: 'Lengthen' },
  TrouserWaist: { subject: 'the trouser waist', decrease: 'Take in', increase: 'Let out' },
  TrouserHem: {
    subject: 'the trouser hem',
    decrease: 'Raise',
    increase: 'Lower',
    where: 'and finish plain, no turn-up',
  },
  Neck: { subject: 'the neck', decrease: 'Take in', increase: 'Let out' },

  // The shop's own card (2026-10-17). Subjects are the shop's words; Joro and Golai are kept as
  // the shop says them rather than guessed at in English, with the plain verbs.
  Pet: { subject: 'the pet', decrease: 'Take in', increase: 'Let out' },
  Seat: { subject: 'the seat', decrease: 'Take in', increase: 'Let out' },
  Collar: { subject: 'the collar', decrease: 'Take in', increase: 'Let out' },
  TrouserLength: { subject: 'the trouser length', decrease: 'Shorten', increase: 'Lengthen' },
  TrouserSeat: { subject: 'the trouser seat', decrease: 'Take in', increase: 'Let out' },
  Thigh: { subject: 'the thigh', decrease: 'Take in', increase: 'Let out' },
  Joro: { subject: 'the joro', decrease: 'Reduce', increase: 'Increase' },
  Moriyu: { subject: 'the moriyu', decrease: 'Narrow', increase: 'Widen' },
  Golai: { subject: 'the golai', decrease: 'Reduce', increase: 'Increase' },
};

/**
 * The measures this module has trade wording for, in the order they are offered at the counter.
 *
 * <p>
 * Derived from {@link measures} rather than written out a second time: the picker and the docket
 * have to agree about what is a known key, and a hand-kept list beside a map is a list that
 * eventually names something the map has lost. <strong>It is what the counter suggests, not what it
 * permits</strong> — the vocabulary is open, and `sentenceFor` prints an unrecognised measure with
 * its own name rather than dropping it, which is what makes the open end safe.
 * </p>
 */
export const knownMeasures: readonly string[] = Object.keys(measures);

/**
 * One alteration row as a sentence.
 *
 * An unrecognised measure is written out with its own name rather than dropped. A docket that
 * silently omits an instruction because the catalogue gained a row the frontend has not been
 * taught about is the worst possible failure here — the garment comes back wrong and nothing on
 * paper says why.
 */
function sentenceFor(detail: AlterationDetail, language: DocketLanguage): string {
  const phrases = phrasesFor(language);
  const figure = detail.sizeInches ?? detail.deltaInches ?? 0;

  const amount = `${formatInches(figure)} ${Math.abs(figure) === 1 ? phrases.inch : phrases.inches}`;

  // English keeps its own map — the one this file's tests pin. The other languages bring theirs.
  const verbs: MeasureVerbs | undefined =
    language === 'English' ? measures[detail.measure] : measuresFor(language)[detail.measure];

  // A finished size is a figure to cut to, not an instruction, so it takes no verb — which also
  // means it needs no tailoring word nobody has reviewed: the measure's subject and the number.
  if ((detail.sizeInches ?? null) !== null) {
    const subject = verbs ? verbs.subject : detail.measure;
    const named =
      language === 'English' ? subject.charAt(0).toUpperCase() + subject.slice(1) : subject;
    const sized = `${named}: ${amount}`;

    return detail.note ? `${sized} (${detail.note})` : sized;
  }

  const shrinking = figure < 0;
  const verb = verbs ? (shrinking ? verbs.decrease : verbs.increase) : null;
  const subject = verbs ? verbs.subject : detail.measure;
  const where = verbs?.where ? ` ${verbs.where}` : '';

  /*
    Word order is the language's, not English's transliterated.

    Gujarati and Hindi put the verb last: "બંને બાઈ કફ થી ૧½ ઇંચ ટૂંકી કરો" is
    *both sleeves / from the cuff / 1½ inch / shorten*. Rendering the English order with translated
    words would be understood and would read as a foreigner talking, which is not what you want on
    the sheet a workshop takes instructions from.
  */
  const core =
    phrases.order === 'verb-last'
      ? `${subject} ${amount} ${verb ?? (shrinking ? phrases.reduce : phrases.increase)}${where ? `,${where}` : ''}`
      : `${verb ?? (shrinking ? phrases.reduce : phrases.increase)} ${subject} ${phrases.by}${amount}${where}`;

  // The note is the counter's own words and is deliberately not translated — it is free text a
  // clerk typed, and a machine rendering of it would be a guess about a garment.
  return detail.note ? `${core} (${detail.note})` : core;
}

/**
 * The full instruction for one garment.
 *
 * Returns an empty string when there is nothing to do, so a caller can decide whether that means
 * "no alteration section" or "nothing outstanding" — those look the same in the data and very
 * different on a docket.
 */
export function alterationNarrative(
  details: readonly AlterationDetail[],
  language: DocketLanguage = 'English',
): string {
  if (details.length === 0) {
    return '';
  }

  return `${details.map((detail) => sentenceFor(detail, language)).join('. ')}.`;
}
